import HttpClient from './HttpClient';
import { OPINION_SERVICE_HOST, OPINIONS_API_PREFIX } from './RestConstants';
import { Opinion, CreateOpinionRequest, VoteRequest, PaginatedResponse } from '../interface/Opinion';

export async function getOpinions(page: number = 1, limit: number = 10, feedType: 'all' | 'following' = 'all'): Promise<PaginatedResponse> {
  const response = await HttpClient.GET({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions?page=${page}&limit=${limit}&feedType=${feedType}`,
  });

  return response.data;
}

export async function getOpinionById(id: string): Promise<Opinion> {
  const response = await HttpClient.GET({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions/${id}`,
  });

  return response.data;
}

export async function createOpinion(opinionData: CreateOpinionRequest): Promise<Opinion> {
  const response = await HttpClient.POST({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions`,
    data: opinionData,
  });

  return response.data;
}

export async function updateOpinion(id: string, content: string, userId: string): Promise<Opinion> {
  const response = await HttpClient.PATCH({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions/${id}`,
    data: { content, userId },
  });

  return response.data;
}

export async function deleteOpinion(id: string, userId: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions/${id}`,
    data: { userId },
  });
}

export async function voteOpinion(id: string, voteData: VoteRequest): Promise<any> {
  const response = await HttpClient.POST({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions/${id}/vote`,
    data: voteData,
  });

  return response.data;
}

export async function submitPeerQuestionVotes(
  id: string,
  responses: number[]
): Promise<{ opinion: Opinion; userSelections: number[] }> {
  const response = await HttpClient.POST({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions/${id}/questions/vote`,
    data: { responses },
  });

  return response.data;
}

export interface OpinionStatsResponse {
  opinion: Opinion;
  stats: {
    agreeCount: number;
    disagreeCount: number;
    commentCount: number;
    totalVotes: number;
    questionStats: {
      questionText: string;
      totalVotes: number;
      options: { text: string; votes: number; percentage: number }[];
    }[];
  };
}

export async function getOpinionStats(id: string): Promise<OpinionStatsResponse> {
  const response = await HttpClient.GET({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/opinions/${id}/stats`,
  });

  return response.data;
}

export async function getUserOpinions(userId: string, limit: number = 20): Promise<{ opinions: Opinion[] }> {
  const response = await HttpClient.GET({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/users/${userId}/opinions?limit=${limit}`,
  });

  return response.data;
}

export async function getUserVotedOpinions(userId: string): Promise<{ opinions: (Opinion & { userVote?: 'agree' | 'disagree' | null })[] }> {
  const response = await HttpClient.GET({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/users/${userId}/voted`,
  });

  return response.data;
}

export async function getUserCommentedOpinions(userId: string): Promise<{ opinions: (Opinion & { userCommentCount?: number; lastCommentDate?: Date | null })[] }> {
  const response = await HttpClient.GET({
    BASE_URL: OPINION_SERVICE_HOST,
    END_POINT: `${OPINIONS_API_PREFIX}/users/${userId}/commented`,
  });

  return response.data;
}
